import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClipboardCheck, Plus, Trash2, GripVertical, FileSignature, Upload, BookOpen, Monitor, FormInput, CheckSquare } from "lucide-react";
import { toast } from "sonner";

const TASK_TYPES = [
  { key: "ESIGN", label: "E-signature", icon: FileSignature, hint: "Contract, NDA, IP assignment" },
  { key: "UPLOAD", label: "Document upload", icon: Upload, hint: "Passport, tax form, bank details" },
  { key: "FORM", label: "Form", icon: FormInput, hint: "Emergency contact, DE&I self-ID" },
  { key: "POLICY", label: "Policy", icon: BookOpen, hint: "Acceptable use, code of conduct" },
  { key: "IT", label: "IT provision", icon: Monitor, hint: "Google, Slack, Jira accounts" },
  { key: "CHECKLIST", label: "Checklist", icon: CheckSquare, hint: "Generic to-do" },
];

const iconFor = (t) => TASK_TYPES.find(x => x.key === t)?.icon || CheckSquare;

export default function OnboardingTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // {name, role, tasks}

  const load = () => api.get("/onboarding-templates").then(r => setTemplates(r.data)).catch(() => setTemplates([]));
  useEffect(() => { load(); }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("action") === "new") {
      newTemplate();
      const p = new URLSearchParams(searchParams); p.delete("action"); setSearchParams(p, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams]);

  const newTemplate = () => setEditing({
    name: "",
    role: "",
    tasks: [
      { task_id: `task_${Date.now()}_a`, title: "Sign employment contract", type: "ESIGN", status: "PENDING" },
      { task_id: `task_${Date.now()}_b`, title: "Upload ID document", type: "UPLOAD", status: "PENDING" },
      { task_id: `task_${Date.now()}_c`, title: "Read acceptable-use policy", type: "POLICY", status: "PENDING" },
    ],
  });

  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/onboarding-templates/${id}`);
    load(); toast.success("Deleted");
  };

  return (
    <div className="space-y-6" data-testid="ob-templates-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-display flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-pink" /> Onboarding templates
          </h1>
          <p className="text-sm text-neutral-500 mt-1">Design reusable checklists — HIRED candidates get the matching template automatically.</p>
        </div>
        <Button onClick={newTemplate} className="btn-primary rounded-full" data-testid="ob-new-template-btn"><Plus className="w-4 h-4 mr-1" /> New template</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {templates.length === 0 && !editing && (
          <div className="col-span-full text-sm text-neutral-500 p-6 bg-white rounded-lg border text-center">
            No custom templates yet. The default 6-task list is used until you build one.
          </div>
        )}
        {templates.map(t => (
          <Card key={t.tpl_id} className="border-0 shadow-sm" data-testid={`ob-tpl-${t.tpl_id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold">{t.name}</h3>
                  {t.role && <div className="text-xs text-neutral-500 mt-1">For {t.role}</div>}
                </div>
                <button onClick={() => del(t.tpl_id)} className="text-neutral-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="mt-3 space-y-1">
                {t.tasks.map((tk, i) => {
                  const Icon = iconFor(tk.type);
                  return (
                    <div key={i} className="text-xs text-neutral-600 flex items-center gap-2">
                      <span className="text-pink font-mono">{String(i+1).padStart(2,"0")}</span>
                      <Icon className="w-3 h-3 text-neutral-400" />
                      <span className="truncate">{tk.title}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {editing && (
        <EditTemplateDialog
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EditTemplateDialog({ template, onClose, onSaved }) {
  const [name, setName] = useState(template.name);
  const [role, setRole] = useState(template.role);
  const [tasks, setTasks] = useState(template.tasks);
  const [dragIdx, setDragIdx] = useState(null);

  const addTask = () => setTasks([...tasks, { task_id: `task_${Date.now()}`, title: "New task", type: "CHECKLIST", status: "PENDING" }]);
  const removeTask = (i) => setTasks(tasks.filter((_, j) => j !== i));
  const updateTask = (i, patch) => setTasks(tasks.map((t, j) => j === i ? { ...t, ...patch } : t));

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const copy = [...tasks];
    const [moved] = copy.splice(dragIdx, 1);
    copy.splice(targetIdx, 0, moved);
    setTasks(copy); setDragIdx(null);
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    if (tasks.length === 0) return toast.error("Add at least one task");
    if (tasks.some(t => !t.title.trim())) return toast.error("Every task needs a title");
    try {
      await api.post("/onboarding-templates", { name, role, tasks });
      toast.success("Template saved");
      onSaved();
    } catch (e) { toast.error("Save failed"); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ob-tpl-dialog">
        <DialogHeader><DialogTitle>New onboarding template</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Template name</Label><Input value={name} onChange={e=>setName(e.target.value)} placeholder="Engineering — standard" data-testid="ob-tpl-name" /></div>
            <div><Label className="text-xs">For role (optional)</Label><Input value={role} onChange={e=>setRole(e.target.value)} placeholder="e.g. Software Engineer" /></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Tasks · drag to reorder</Label>
              <Button size="sm" variant="ghost" onClick={addTask} className="text-xs" data-testid="ob-tpl-add-task"><Plus className="w-3 h-3 mr-1" /> Add task</Button>
            </div>
            <div className="space-y-1.5">
              {tasks.map((t, i) => {
                const Icon = iconFor(t.type);
                return (
                  <div
                    key={t.task_id}
                    draggable
                    onDragStart={() => onDragStart(i)}
                    onDragOver={onDragOver}
                    onDrop={() => onDrop(i)}
                    data-testid={`ob-tpl-task-${i}`}
                    className={`flex items-center gap-2 p-2.5 bg-neutral-50 rounded-lg border ${dragIdx===i ? "opacity-40" : "border-transparent"} hover:border-neutral-200`}>
                    <GripVertical className="w-4 h-4 text-neutral-300 shrink-0 cursor-grab active:cursor-grabbing" data-testid={`ob-tpl-drag-${i}`} />
                    <span className="text-pink font-mono text-xs w-6 shrink-0">{String(i+1).padStart(2,"0")}</span>
                    <Icon className="w-4 h-4 text-neutral-400 shrink-0" />
                    <Input
                      value={t.title}
                      onChange={e=>updateTask(i, { title: e.target.value })}
                      className="h-8 flex-1"
                      data-testid={`ob-tpl-task-title-${i}`}
                    />
                    <select
                      value={t.type}
                      onChange={e=>updateTask(i, { type: e.target.value })}
                      className="text-xs h-8 px-2 rounded-md border border-neutral-200 shrink-0"
                      data-testid={`ob-tpl-task-type-${i}`}>
                      {TASK_TYPES.map(tt => <option key={tt.key} value={tt.key}>{tt.label}</option>)}
                    </select>
                    <button onClick={() => removeTask(i)} className="text-neutral-300 hover:text-red-500 shrink-0" data-testid={`ob-tpl-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-neutral-400 mt-2">Task type icons: {TASK_TYPES.map(tt => <span key={tt.key} className="ml-2"><tt.icon className="w-3 h-3 inline mr-0.5" />{tt.label}</span>)}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="btn-primary" onClick={save} data-testid="ob-tpl-save">Save template</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
